import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import ClipLoader from 'react-spinners/ClipLoader'
import { toast } from 'react-toastify'
import { Icon as IconifyIcon } from '@iconify/react'
import PrivateLayout from '../components/PrivateLayout'
import Footer from '../components/Footer'
import OrganizationSummary from '../components/OrganizationSummary'
import { useAddWorkMutation, useLazyGetWorkQuery, useUpdateWorkMutation } from '../store/api'
import { FilterOption, OrganizationWorkData, WorkUpdateData } from '../types'
import { convertWorkType } from '../utils'

const languageOptions: FilterOption[] = [
  {
    label: 'Select language',
    value: '',
  },
  {
    label: 'English',
    value: 'en',
  },
  {
    label: 'French',
    value: 'fr',
  },
  {
    label: 'Japanese',
    value: 'ja',
  },
]

const EditWork = (): JSX.Element => {
  const navigate = useNavigate()

  const [updateWork] = useUpdateWorkMutation()

  const { workId } = useParams()
  const [searchParams] = useSearchParams()
  const [addWork] = useAddWorkMutation()
  const [getWork] = useLazyGetWorkQuery()

  const [title, setTitle] = useState<string>('')
  const [workProductAbstract, setWorkProductAbstract] = useState<string>('')
  const [publisher, setPublisher] = useState<string>('')
  const [doi, setDoi] = useState<string>('')
  const [isbn, setIsbn] = useState<string>('')
  const [url, setUrl] = useState<string>('')
  const [language, setLanguage] = useState<string>('')
  const [page, setPage] = useState<string>('')
  const [volume, setVolume] = useState<string>('')
  const [issue, setIssue] = useState<string>('')
  const [containerTitle, setContainerTitle] = useState<string>('')
  const [publicationDate, setPublicationDate] = useState<string>('')
  const [printedDate, setPrintedDate] = useState<string>('')

  const [isApiRequestInProgress, setIsApiRequestInProgress] = useState<boolean>(false)
  const [isGetWorkApiRequestInProgress, setIsGetWorkApiRequestInProgress] = useState<boolean>(false)

  const isFormValid = useMemo(() => {
    return title && workProductAbstract && publisher && language && printedDate && publicationDate && url
  }, [title, workProductAbstract, publisher, language, printedDate, publicationDate, doi, isbn, url])

  const workType = useMemo(() => {
    return searchParams.get('type') ?? 'journal-article'
  }, [searchParams])

  const loadData = useCallback(async () => {
    if (workId) {
      // Get work details
      try {
        setIsGetWorkApiRequestInProgress(true)

        const workDetails: OrganizationWorkData = await getWork(workId).unwrap()

        if (workDetails) {
          setTitle(workDetails.workProduct.title)
          setDoi(workDetails.workProduct.doi)
          setIsbn(workDetails.workProduct.isbn)
          setUrl(workDetails.workProduct.url)
          setLanguage(workDetails.workProduct.language)
          setWorkProductAbstract(workDetails.workProduct.workProductAbstract)
          setPublisher(workDetails.workProduct.publisher)

          if (workDetails.workProduct.containerTitle) {
            setContainerTitle(workDetails.workProduct.containerTitle)
          }

          if (workDetails.workProduct.page) {
            setPage(workDetails.workProduct.page)
          }

          if (workDetails.workProduct.volume) {
            setVolume(workDetails.workProduct.volume)
          }

          if (workDetails.workProduct.issue) {
            setIssue(workDetails.workProduct.issue)
          }

          if (workDetails.workProduct.printedDate) {
            setPrintedDate(moment(workDetails.workProduct.printedDate).format('YYYY-MM-DD'))
          }

          if (workDetails.workProduct.publicationDate) {
            setPublicationDate(moment(workDetails.workProduct.publicationDate).format('YYYY-MM-DD'))
          }
        }

        setIsGetWorkApiRequestInProgress(false)
      } catch (e) {
        console.error(e)
        setIsGetWorkApiRequestInProgress(false)
      }
    }
  }, [workId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const onSaveChangesClicked = useCallback(async () => {
    if (!isFormValid || !workId) {
      return
    }

    const request: WorkUpdateData = {
      title,
      workProductAbstract,
      workProductType: workType,
      doi,
      isbn,
      url,
      language,
      publisher,
      containerTitle,
      publicationDate: moment(publicationDate, 'YYYY-MM-DD').toDate(),
      printedDate: moment(printedDate, 'YYYY-MM-DD').toDate(),
      ...(workType === 'journal-article'
        ? {
            page,
            volume,
            issue,
          }
        : {}),
    }

    try {
      setIsApiRequestInProgress(true)
      await updateWork({ data: request, workId }).unwrap()

      setIsApiRequestInProgress(false)
      toast('Successfully updated the work')
      navigate(-1)
    } catch (e) {
      console.error(e)
      toast.error('Failed to update the work')
      setIsApiRequestInProgress(false)
    }
  }, [
    title,
    workProductAbstract,
    containerTitle,
    publisher,
    language,
    printedDate,
    publicationDate,
    doi,
    isbn,
    url,
    page,
    volume,
    issue,
  ])

  return (
    <PrivateLayout>
      <div className="content">
        <OrganizationSummary />

        <div className="d-flex align-items-center mb-4">
          <h3>Work</h3>
          {isGetWorkApiRequestInProgress ? (
            <div className="spinner-wrapper ms-3" style={{ width: 'unset' }}>
              <ClipLoader color="#e5780b" loading={isGetWorkApiRequestInProgress} size={20} />
            </div>
          ) : null}
        </div>

        <h3 className="mb-4">{convertWorkType(workType)}</h3>
        <div className="row">
          <div className="col-xl-9">
            <form className="row g-3 mb-6">
              <div className="col-12">
                <div className="form-floating">
                  <input
                    className="form-control"
                    id="title"
                    type="text"
                    placeholder="Title"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value)
                    }}
                  />
                  <label htmlFor="title">Title</label>
                </div>
              </div>

              <div className="col-12">
                <div className="form-floating">
                  <textarea
                    className="form-control"
                    id="workAbstract"
                    placeholder=""
                    style={{ height: '150px' }}
                    value={workProductAbstract}
                    onChange={(e) => {
                      setWorkProductAbstract(e.target.value)
                    }}
                  ></textarea>
                  <label htmlFor="workAbstract">Abstract</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4 gy-6">
                <div className="form-floating">
                  <div className="form-floating">
                    <input
                      className="form-control"
                      id="publisher"
                      type="text"
                      placeholder="Publisher"
                      value={publisher}
                      onChange={(e) => {
                        setPublisher(e.target.value)
                      }}
                    />
                    <label htmlFor="publisher">Publisher</label>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-md-8 gy-6">
                <div className="form-floating">
                  <input
                    className="form-control"
                    id="containerTitle"
                    type="text"
                    placeholder="Container Title"
                    value={containerTitle}
                    onChange={(e) => {
                      setContainerTitle(e.target.value)
                    }}
                  />
                  <label htmlFor="containerTitle">Container Title</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="language"
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value)
                    }}
                  >
                    {languageOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="language">Language</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="flatpickr-input-container">
                  <div className="form-floating">
                    <input
                      className="form-control datetimepicker"
                      id="printedDate"
                      type="date"
                      placeholder="Printed date"
                      data-options='{"disableMobile":true}'
                      value={printedDate}
                      onChange={(e) => {
                        setPrintedDate(e.target.value)
                      }}
                    />
                    <label className="ps-6" htmlFor="printedDate">
                      Printed
                    </label>
                    <IconifyIcon
                      icon="uil:calendar-alt"
                      className="uil uil-calendar-alt flatpickr-icon text-700 me-2"
                    />
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-md-4">
                <div className="flatpickr-input-container">
                  <div className="form-floating">
                    <input
                      className="form-control datetimepicker"
                      id="publicationDate"
                      type="date"
                      placeholder="Published"
                      data-options='{"disableMobile":true}'
                      value={publicationDate}
                      onChange={(e) => {
                        setPublicationDate(e.target.value)
                      }}
                    />
                    <label className="ps-6" htmlFor="publicationDate">
                      Published
                    </label>
                    <IconifyIcon
                      icon="uil:calendar-alt"
                      className="uil uil-calendar-alt flatpickr-icon text-700 me-2"
                    />
                  </div>
                </div>
              </div>

              {workType === 'journal-article' ? (
                <>
                  <div className="col-sm-6 col-md-4">
                    <div className="form-floating">
                      <input
                        className="form-control"
                        id="pages"
                        type="text"
                        placeholder="Pages"
                        value={page}
                        onChange={(e) => {
                          setPage(e.target.value)
                        }}
                      />
                      <label htmlFor="pages">Pages</label>
                    </div>
                  </div>
                  <div className="col-sm-6 col-md-4">
                    <div className="form-floating">
                      <input
                        className="form-control"
                        id="volume"
                        type="text"
                        placeholder="Volume"
                        value={volume}
                        onChange={(e) => {
                          setVolume(e.target.value)
                        }}
                      />
                      <label htmlFor="volume">Volume</label>
                    </div>
                  </div>
                  <div className="col-sm-6 col-md-4">
                    <div className="form-floating">
                      <input
                        className="form-control"
                        id="issue"
                        type="text"
                        placeholder="Issue"
                        value={issue}
                        onChange={(e) => {
                          setIssue(e.target.value)
                        }}
                      />
                      <label htmlFor="issue">Issue</label>
                    </div>
                  </div>
                </>
              ) : null}

              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <input
                    className="form-control"
                    id="doi"
                    type="text"
                    placeholder="DOI"
                    value={doi}
                    onChange={(e) => {
                      setDoi(e.target.value)
                    }}
                  />
                  <label htmlFor="doi">DOI</label>
                </div>
              </div>
              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <input
                    className="form-control"
                    id="issn"
                    type="text"
                    placeholder="ISSN"
                    value={isbn}
                    onChange={(e) => {
                      setIsbn(e.target.value)
                    }}
                  />
                  <label htmlFor="issn">ISBN</label>
                </div>
              </div>
              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <input
                    className="form-control"
                    id="link"
                    type="text"
                    placeholder="URL"
                    value={url}
                    onChange={(e) => {
                      setUrl(e.target.value)
                    }}
                  />
                  <label htmlFor="link">URL</label>
                </div>
              </div>

              <div className="col-12 gy-6">
                <div className="row g-3 justify-content-end">
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-phoenix-primary px-5"
                      onClick={() => {
                        navigate(-1)
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-primary px-3 px-sm-3"
                      disabled={!isFormValid}
                      style={{ width: '150px' }}
                      onClick={() => {
                        onSaveChangesClicked()
                      }}
                    >
                      {isApiRequestInProgress ? (
                        <ClipLoader color="white" loading={isApiRequestInProgress} size={15} />
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <Footer />
      </div>
    </PrivateLayout>
  )
}

export default EditWork
